/**
 * HR Performance Review Framework (hr_v1)
 *
 * Parallel to the OPTICS path. Implements the rules in CLAUDE.md §B–§D:
 *   - EPR Form  (OKR 30% + KPI 40% + Core 20% + Leadership 10%)
 *   - 360 Form  (6 company values, anonymous)
 *
 * The OPTICS implementation in this repo serves as the 360 channel of the
 * OPTICS-framework cycles. The hr_v1 path is additive — it does not change,
 * extend, or retrofit the OPTICS types.
 *
 * Source of truth: Performance Review Framework.docx (HR drive).
 */

import type { Timestamp } from 'firebase/firestore';

// ─── Scoring scales (§C.3, §C.4, §D) ─────────────────────────────────────────

export type Score1to5 = 1 | 2 | 3 | 4 | 5;

export interface ScaleBand {
  score: Score1to5;
  label: string;
  meaning: string;
  achievementMin?: number;  // % min (inclusive)
  achievementMax?: number;  // % max (inclusive)
}

export interface ScaleDef {
  kind: 'kpi' | 'okr' | 'value';
  bands: ScaleBand[];
}

export const KPI_SCALE: ScaleDef = {
  kind: 'kpi',
  bands: [
    { score: 5, label: 'Exceptional',    meaning: '>= 110% of target', achievementMin: 110 },
    { score: 4, label: 'Exceeded',       meaning: '100 - 109% of target', achievementMin: 100, achievementMax: 109 },
    { score: 3, label: 'Meet',           meaning: '90 - 99% of target',  achievementMin: 90,  achievementMax: 99 },
    { score: 2, label: 'Needs improvement', meaning: '75 - 89% of target', achievementMin: 75, achievementMax: 89 },
    { score: 1, label: 'Unsatisfactory', meaning: '< 75% of target', achievementMax: 74 },
  ],
};

export const OKR_SCALE: ScaleDef = {
  kind: 'okr',
  bands: [
    { score: 5, label: 'Far exceeded',  meaning: '100% of a genuinely hard goal', achievementMin: 100 },
    { score: 4, label: 'Exceeded',      meaning: '80 - 99%',  achievementMin: 80,  achievementMax: 99 },
    { score: 3, label: 'Met',           meaning: '60 - 79%',  achievementMin: 60,  achievementMax: 79 },
    { score: 2, label: 'Some progress', meaning: '40 - 59%',  achievementMin: 40,  achievementMax: 59 },
    { score: 1, label: 'Little / none', meaning: '< 40%',     achievementMax: 39 },
  ],
};

export const VALUE_SCALE: ScaleDef = {
  kind: 'value',
  bands: [
    { score: 5, label: 'Role Model',         meaning: 'Consistently demonstrates the behaviour, strengthens team, positively influences others.' },
    { score: 4, label: 'Strong',             meaning: 'Demonstrates regularly with minor gaps. Reliable.' },
    { score: 3, label: 'Meets Expectation',  meaning: 'Adequate but not always proactive or consistent.' },
    { score: 2, label: 'Needs Improvement',  meaning: 'Inconsistent. Requires reminders or support.' },
    { score: 1, label: 'Below Standard',     meaning: 'Rarely demonstrates. Creates risk, delay, conflict, rework.' },
  ],
};

// ─── Scale configuration (template-driven) ──────────────────────────────────
//
// The MECHANISM is configurable; the VALUES are not free. The default below
// is the canonical 1–5 scale per CLAUDE.md §C. Switching a template to /10
// is a §I forbidden change: it requires HR sign-off, an updated framework
// document, and a CLAUDE.md update in the same commit.

export interface EvidenceTriggerConfig {
  /** Scores >= this require notes_evidence (top of the scale). */
  highMin: number;
  /** Scores <= this require notes_evidence (bottom of the scale). */
  lowMax: number;
}

export interface EPRScaleConfig {
  /** Lowest selectable score (inclusive). */
  min: number;
  /** Highest selectable score (inclusive). Displayed as "/ {max}.00". */
  max: number;
  /** Replaces the hardcoded "5 or <= 2" rule (§C.7) on non-default scales. */
  evidenceTrigger: EvidenceTriggerConfig;
}

/** Canonical scale per CLAUDE.md §C — 1–5, evidence on 5 and <= 2. */
export const DEFAULT_SCALE_CONFIG: EPRScaleConfig = {
  min: 1,
  max: 5,
  evidenceTrigger: { highMin: 5, lowMax: 2 },
};

// ─── Competency definitions (§C.5, §C.6) ────────────────────────────────────

export interface CompetencyDef {
  key: string;        // stable id, e.g. 'core_accountability'
  name: string;       // 'Accountability'
  description: string;
  /**
   * Rating guideline text per score. Keyed by score value — covers the
   * template's full scale (1–5 on the default scale; a /10 template must
   * provide all ten entries).
   */
  rubric: Record<number, string>;
}

export const CORE_COMPETENCIES: CompetencyDef[] = [
  { key: 'core_accountability',
    name: 'Accountability',
    description: 'Owns mistakes, delivers on commitments, follows through without being chased, acts honestly even when no one is watching.',
    rubric: {
      1: 'Blames others. Hides errors. Misses deadlines repeatedly. Cannot be trusted without supervision.',
      2: 'Misses commitments now and then. Makes excuses. Needs reminding. Sometimes hides small mistakes.',
      3: 'Generally delivers on time. Owns up when asked. No major issues with honesty or follow-through.',
      4: 'Reliable. Admits errors without being pushed. Takes responsibility. Keeps promises.',
      5: 'Does what they say, every time. Owns mistakes immediately. Follows through without being chased.',
    },
  },
  { key: 'core_communication',
    name: 'Communication',
    description: 'Shares information clearly and timely, adapts style to the audience, gives/receives feedback constructively.',
    rubric: {
      1: 'Confusing or misleading. Avoids difficult conversations. Rejects feedback.',
      2: 'Often unclear or leaves out key details. Misunderstands. Gets defensive.',
      3: 'Gets the message across, but can be unclear sometimes. Accepts feedback but doesn\'t always act.',
      4: 'Clear and timely. Easy to understand. Handles feedback well. Shares useful input.',
      5: 'Keeps everyone in the loop. Adapts message. Gives tough feedback kindly. Takes feedback and changes behaviour.',
    },
  },
  { key: 'core_collaboration',
    name: 'Collaboration & Teamwork',
    description: 'Puts team success first, helps others without being asked, shares credit fairly, works well across groups.',
    rubric: {
      1: 'Works alone when team input is needed. Doesn\'t share information. Blames others.',
      2: 'Keeps to themselves. Rarely helps others. Needs encouragement to collaborate.',
      3: 'Does their part. Cooperates when asked. Doesn\'t go above and beyond.',
      4: 'Reliable team player. Willingly helps. Shares credit. Good to work with.',
      5: 'Puts team first every time. Jumps in to help. Celebrates others\' wins.',
    },
  },
  { key: 'core_work_quality',
    name: 'Work Quality & Timeliness',
    description: 'Produces accurate, error-free work consistently and meets deadlines without rushing or cutting corners.',
    rubric: {
      1: 'Poor quality. Constant errors. Repeatedly misses deadlines without good reason.',
      2: 'Regular mistakes or rework. Frequently misses deadlines.',
      3: 'Generally accurate with occasional minor errors. Meets deadlines most of the time.',
      4: 'Very few mistakes. Always meets deadlines. Reliable and consistent.',
      5: 'Work is spot on. Always on time or ahead. Sets the quality standard for others.',
    },
  },
  { key: 'core_adaptability',
    name: 'Adaptability, Learning & Problem-Solving',
    description: 'Embraces change, picks up new skills quickly, learns from feedback, finds solutions to challenges.',
    rubric: {
      1: 'Blocks change. No interest in learning. Escalates everything. Avoids solving problems.',
      2: 'Resists change. Slow to pick up new ways. Struggles to find root causes. Gives up easily.',
      3: 'Accepts change. Learns at expected pace. Solves routine problems but needs support for tricky ones.',
      4: 'Adapts well. Applies feedback. Handles most problems with minimal help. Keeps skills current.',
      5: 'Leads through change. Seeks new skills. Solves complex problems that stump others.',
    },
  },
];

export const LEADERSHIP_SKILLS: CompetencyDef[] = [
  { key: 'lead_people_dev',
    name: 'People Development',
    description: 'Helps others grow. Coaches, mentors, invests time in developing people around them.',
    rubric: {
      1: 'Ignores development completely. No feedback given. People feel stuck.',
      2: 'Rarely talks about development. Feedback is vague or too late.',
      3: 'Does basic coaching when asked. Some investment in others.',
      4: 'Good coach. Checks in on development goals. Gives useful feedback.',
      5: 'Actively develops everyone. Regular, specific feedback. Spots and nurtures potential.',
    },
  },
  { key: 'lead_decision_making',
    name: 'Decision Making',
    description: 'Makes sound, timely decisions. Sees the big picture. Thinks ahead about impact.',
    rubric: {
      1: 'Poor judgment. No strategic awareness. Doesn\'t think ahead.',
      2: 'Decisions are shaky or take too long. Focused only on today.',
      3: 'Decent decisions but sometimes slow or hesitant.',
      4: 'Good decisions most of the time. Thinks ahead about consequences.',
      5: 'Makes tough calls quickly and confidently. Always thinks two steps ahead.',
    },
  },
  { key: 'lead_proactiveness',
    name: 'Proactiveness & Ownership',
    description: 'Takes initiative. Doesn\'t wait to be told. Owns outcomes, good or bad.',
    rubric: {
      1: 'Avoids work. Blames others. Ignores problems until they blow up.',
      2: 'Waits for instructions. Avoids responsibility when things go wrong.',
      3: 'Does what\'s asked. Sometimes needs a push. Owns up when called out.',
      4: 'Takes initiative most of the time. Reliable. Generally self-starts.',
      5: 'Sees what needs doing and does it. Owns mistakes openly. Follows through.',
    },
  },
  { key: 'lead_collaboration',
    name: 'Collaboration',
    description: 'Works well with others. Puts team success first. Shares information and credit.',
    rubric: {
      1: 'Works alone even when team input is needed. Blames others.',
      2: 'Keeps to themselves. Rarely helps others.',
      3: 'Does their part. Cooperates when asked.',
      4: 'Reliable team player. Willingly helps out. Easy to work with.',
      5: 'Puts team first. Jumps in to help. Shares information freely.',
    },
  },
  { key: 'lead_influence',
    name: 'Influence & Inspires',
    description: 'Gets buy-in without pulling rank. Persuades through logic and relationships. Motivates others.',
    rubric: {
      1: 'Tries to force things or gives up too easily. Creates friction. Drains energy.',
      2: 'Struggles to persuade. Complains rather than influences.',
      3: 'Can convince sometimes, but struggles with resistance.',
      4: 'Persuasive and diplomatic. Usually gets buy-in. Positive presence.',
      5: 'Gets people on board without authority. Builds strong cases. Energises others.',
    },
  },
];

// ─── Company values (§D.2) ──────────────────────────────────────────────────

export type CompanyValueKey =
  | 'take_ownership'
  | 'be_progressive'
  | 'apply_transparency'
  | 'encourage_ideas'
  | 'empower_community'
  | 'practice_sustainability';

export const COMPANY_VALUE_KEYS: CompanyValueKey[] = [
  'take_ownership',
  'be_progressive',
  'apply_transparency',
  'encourage_ideas',
  'empower_community',
  'practice_sustainability',
];

export const COMPANY_VALUE_NAMES: Record<CompanyValueKey, string> = {
  take_ownership:         'Take Ownership',
  be_progressive:         'Be Progressive',
  apply_transparency:     'Apply Transparency',
  encourage_ideas:        'Encourage Ideas',
  empower_community:      'Empower Community',
  practice_sustainability:'Practice Sustainability',
};

// ─── Weights (§C.1) — canonical, do not edit without HR sign-off ────────────

export interface EPRWeights {
  okr: number;
  kpi: number;
  core: number;
  leadership: number;  // 0 for non-people-managers, redistributed proportionally
}

export const DEFAULT_WEIGHTS_PEOPLE_MANAGER: EPRWeights = {
  okr: 0.30, kpi: 0.40, core: 0.20, leadership: 0.10,
};

/**
 * Non-people-manager weights — Leadership removed and 10% redistributed
 * proportionally across OKR / KPI / Core. Decision per CLAUDE.md §J #3:
 * "Skip Leadership, redistribute proportional."
 *
 *   OKR  = 30 / 90 = 0.3333
 *   KPI  = 40 / 90 = 0.4444
 *   Core = 20 / 90 = 0.2222
 */
export const DEFAULT_WEIGHTS_NON_MANAGER: EPRWeights = {
  okr: 0.3333, kpi: 0.4444, core: 0.2222, leadership: 0,
};

// ─── 360 visibility policy (§D.1) ───────────────────────────────────────────

export type Review360Relationship = 'manager' | 'peer' | 'subordinate';

export type ReviewerVisibility = 'attributed' | 'anonymous';

/**
 * Default per-relationship visibility (CLAUDE.md §D.1).
 * - manager:     attributed (single reviewer; anonymising adds nothing)
 * - peer:        anonymous  (default-safe; HR can override per cycle)
 * - subordinate: anonymous  (usually small group; easy to re-identify)
 *
 * Override via EPRFrameworkTemplate.reviewerVisibility.
 */
export const DEFAULT_REVIEWER_VISIBILITY: Record<Review360Relationship, ReviewerVisibility> = {
  manager:     'attributed',
  peer:        'anonymous',
  subordinate: 'anonymous',
};

// ─── Templates ──────────────────────────────────────────────────────────────

export type EPRFrameworkVersion = 'hr_v1';

/**
 * Where a component's items come from when the form is filled:
 *   - 'okrTracker'    — synced from the OKR Tracker; the employee SELECTS
 *                       which of their KRs apply (manual pick, source of
 *                       truth stays okr.rascalrepublic.com)
 *   - 'employeeInput' — the employee types the items themselves (KPI rows
 *                       per the canonical EPR Form)
 *   - 'template'      — fixed list defined on the template (Core
 *                       Competencies, Leadership Skills)
 */
export type ComponentSource = 'okrTracker' | 'employeeInput' | 'template';

export const DEFAULT_COMPONENT_SOURCES: Record<'okr' | 'kpi' | 'core' | 'leadership', ComponentSource> = {
  okr:        'okrTracker',
  kpi:        'employeeInput',
  core:       'template',
  leadership: 'template',
};

export type EPRTemplateStatus = 'draft' | 'active' | 'archived';

export interface EPRFrameworkTemplate {
  id: string;
  name: string;
  version: EPRFrameworkVersion;
  weights: EPRWeights;
  coreCompetencies: CompetencyDef[];
  leadershipSkills: CompetencyDef[];
  scoringScales: { kpi: ScaleDef; okr: ScaleDef; value: ScaleDef };
  appliesToLevels: Array<'general' | 'supervisor' | 'dept_head'>;

  /** Absent → DEFAULT_SCALE_CONFIG (canonical 1–5 per §C). */
  scaleConfig?: EPRScaleConfig;

  /** Absent (or per-key absent) → DEFAULT_COMPONENT_SOURCES. */
  componentSources?: Partial<Record<'okr' | 'kpi' | 'core' | 'leadership', ComponentSource>>;

  /**
   * Lifecycle: only 'active' templates are offered at review creation.
   * Editing an active template must bump `templateVersion` — launched
   * reviews are unaffected because they carry an EPRTemplateSnapshot.
   */
  status?: EPRTemplateStatus;
  templateVersion?: number;

  /**
   * Override the default visibility per relationship for 360 reviewers.
   * Any relationship not specified falls back to DEFAULT_REVIEWER_VISIBILITY.
   * Aggregators MUST consult the effective policy before exposing identity
   * (CLAUDE.md §D.1).
   */
  reviewerVisibility?: Partial<Record<Review360Relationship, ReviewerVisibility>>;

  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── EPR Submission (§C) ────────────────────────────────────────────────────

export type EPRComponent = 'okr' | 'kpi' | 'core' | 'leadership';

export type EPRSubmissionStatus =
  | 'draft'
  | 'employee_submitted'
  | 'manager_submitted'
  | 'locked';

export interface EPRItem {
  itemId: string;
  component: EPRComponent;
  itemName: string;
  description?: string;

  // OKR / KPI only
  target?: string;
  actual?: string;
  achievementPct?: number;

  /**
   * Who created the item. 'launch' = seeded by HR at launch (legacy path),
   * 'employee' = added by the employee in the magic-link form (OKR pick /
   * manual KPI row). Employee-created items are removable while draft;
   * launch/template items are not.
   */
  addedBy?: 'launch' | 'employee';

  /**
   * Scores are integers within the submission's scale snapshot
   * (templateSnapshot.scaleConfig, default 1–5 per §C). Validated by
   * eprScoreEngine.isValidScore — typed as number so a /10 template
   * (post HR sign-off, §I) needs no type change.
   */
  employeeSelfScore?: number;
  managerScore?: number;
  /** average(employeeSelfScore, managerScore) per CLAUDE.md §C.2 */
  itemFinalScore?: number;

  /**
   * Required when score = 5 or <= 2 per CLAUDE.md §C.7.
   * Submission MUST be blocked client-side and server-side if missing.
   */
  notesEvidence?: string;

  // Calibration overlay — never overwrites the raw scores above
  calibrationAdjustedScore?: number;
  calibrationReason?: string;
}

export interface EPRComponentScores {
  rawOkr?: number;
  rawKpi?: number;
  rawCore?: number;
  rawLeadership?: number;

  /** Weighted total before calibration. Computed: sum(rawX * weights.X) */
  rawTotal?: number;

  /** Set by calibration; null if untouched. */
  calibratedTotal?: number;

  /** Resolved final: calibratedTotal ?? rawTotal */
  finalTotal?: number;
}

/**
 * Header snapshot for the canonical EPR Form (per `EPR Form.docx`:
 * Employee Name · Position · Department · Review Period · Supervisor
 * Name · Review Date). Captured at launch from the directory + cycle so
 * neither HR nor the employee types any of it — the magic-link form
 * renders it read-only. Review Date is not stored: it is the
 * submittedBy*At timestamp on the submission itself.
 */
export interface EPRSubmissionHeader {
  employeeName: string;
  /** Job title from the M365-synced directory at launch time. */
  position?: string;
  /** Unit/department name from the directory at launch time. */
  department?: string;
  supervisorName: string;
  /** Display label, e.g. "H1 2026". */
  reviewPeriodLabel?: string;
  /** ISO date (yyyy-mm-dd) — cycle startDate. */
  reviewPeriodFrom?: string;
  /** ISO date (yyyy-mm-dd) — cycle endDate. */
  reviewPeriodTo?: string;
}

/**
 * Frozen copy of everything the magic-link form needs from the template,
 * stamped on the submission at launch. Mid-cycle template edits never
 * change a live form, and historical submissions render exactly as they
 * were scored — including the scale they were scored on.
 */
export interface EPRTemplateSnapshot {
  templateId: string;
  templateName: string;
  templateVersion?: number;
  scaleConfig: EPRScaleConfig;
  componentSources: Record<EPRComponent, ComponentSource>;
  scoringScales: { kpi: ScaleDef; okr: ScaleDef };
  coreCompetencies: CompetencyDef[];
  leadershipSkills: CompetencyDef[];
}

export interface EPRSubmission {
  id: string;             // composite `{cycleId}_{employeeId}` for easy lookup
  cycleId: string;
  employeeId: string;
  supervisorId: string;
  templateId: string;
  weights: EPRWeights;    // snapshot at cycle launch — survives template edits
  /**
   * Optional — submissions launched before snapshots existed won't have
   * it; readers fall back to the §C constants (DEFAULT_SCALE_CONFIG,
   * CORE_COMPETENCIES, LEADERSHIP_SKILLS, KPI_SCALE, OKR_SCALE).
   */
  templateSnapshot?: EPRTemplateSnapshot;
  status: EPRSubmissionStatus;

  /**
   * Canonical form header (see EPRSubmissionHeader). Optional — older
   * submissions launched before the snapshot existed won't have it; the
   * form falls back to the token's recipient fields.
   */
  header?: EPRSubmissionHeader;

  okrItems: EPRItem[];
  kpiItems: EPRItem[];
  /** Optional reference if KPIs were sourced from a future catalog. Phase-1: not used. */
  kpiCatalogId?: string;
  coreItems: EPRItem[];
  leadershipItems: EPRItem[];  // empty array for non-people-managers

  scores: EPRComponentScores;

  submittedByEmployeeAt?: Timestamp;
  submittedByManagerAt?: Timestamp;
  lockedAt?: Timestamp;

  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── 360 Submission (HR-framework path, §D) ─────────────────────────────────

/**
 * 360 submission for the hr_v1 path.
 *
 * Visibility is configurable per relationship via EPRFrameworkTemplate
 * (CLAUDE.md §D.1):
 *   - 'attributed' relationships: reviewerId and reviewerName ARE stored and
 *     may be returned to employee/manager
 *   - 'anonymous' relationships: reviewerId and reviewerName MUST be omitted
 *     from any read returned outside HR. The aggregator is the single chokepoint
 *     that enforces this — see §D.1 and MIN_REVIEWERS_FOR_EXPOSURE below.
 *
 * The reviewer's identity is always available on the source ReviewInvitation
 * via `invitationId`. That is the audit trail; never strip it from there.
 */
export interface Review360Submission {
  id: string;
  cycleId: string;
  revieweeId: string;
  invitationId: string;        // FK to ReviewInvitation (token-based, identity here)
  relationship: Review360Relationship;

  /**
   * Effective visibility at submission time. Snapshot of the cycle's policy
   * for this relationship — survives later template edits. Aggregators read
   * this field, not the live template.
   */
  effectiveVisibility: ReviewerVisibility;

  /**
   * Reviewer identity at submission time. Populated only when
   * effectiveVisibility === 'attributed'. Always omitted for 'anonymous'.
   * Aggregators serving non-HR roles MUST NOT return these fields when
   * effectiveVisibility === 'anonymous'.
   */
  reviewerId?: string;
  reviewerName?: string;

  /** 1–5 score per company value. Blank allowed when reviewer cannot judge. */
  valueScores: Partial<Record<CompanyValueKey, Score1to5>>;

  strengths?: string;
  thingsToImprove?: string;
  overallComments?: string;

  /**
   * Absent while the reviewer is still drafting; set exactly once by
   * finaliseSubmission. Both the service guard and firestore.rules treat
   * "field present" as finalised/sealed — never write it at create time.
   */
  submittedAt?: Timestamp;
  createdAt: Timestamp;
}

// ─── Visibility resolver helper (§D.1) ──────────────────────────────────────

/**
 * Resolve effective reviewer visibility for a given relationship under a
 * template's policy. Used at invitation issue time and at submission write
 * time so the result can be snapshotted on the submission doc.
 */
export function resolveReviewerVisibility(
  relationship: Review360Relationship,
  templateOverride?: Partial<Record<Review360Relationship, ReviewerVisibility>>,
): ReviewerVisibility {
  return templateOverride?.[relationship] ?? DEFAULT_REVIEWER_VISIBILITY[relationship];
}

// ─── Aggregator guardrail constant (§D.1) ───────────────────────────────────

export const MIN_REVIEWERS_FOR_EXPOSURE = 3;
